class NavbarPU {
  constructor(el) {
    // Performance mark for initialization
    performance.mark("navbar-init-start");

    this.navbar = el;

    // Cache all frequently accessed DOM elements upfront
    this.cacheElements();

    // Track component state
    this.wasMobile = this.isMobile();
    this.isMenuOpen = false;
    this.isDrawerOpen = false;
    this.MOBILE_MAX_WIDTH = 1250;

    // Accessibility focus management
    this.lastFocusedElement = null;
    this.currentActiveDropdown = null;
    this.focusTrapActive = false;

    // Throttling for resize events
    this.resizeThrottleId = null;
    this.resizeThrottleDelay = 16; // ~60fps

    // Create shadow element for drawer mode
    this.createShadowElement();

    // Initialize with a slight delay for non-critical parts
    this.initCriticalFeatures();
    requestIdleCallback(() => this.initNonCriticalFeatures(), {
      timeout: 1000,
    });

    // Performance mark for core initialization
    performance.mark("navbar-init-end");
    performance.measure("navbar-init", "navbar-init-start", "navbar-init-end");
  }

  cacheElements() {
    // Cache all selector-based elements for better performance
    this.menu = this.navbar.querySelector("[data-navbar-menu]");
    this.toggler = this.navbar.querySelector("[data-navbar-toggler]");
    this.iconOpen = this.navbar.querySelector('[data-navbar-icon="open"]');
    this.iconClose = this.navbar.querySelector('[data-navbar-icon="close"]');
    this.sectionToggles = this.navbar.querySelectorAll(
      "[data-navbar-section-toggle]"
    );
    this.drawer = this.navbar.querySelector("[data-navbar-drawer]");
    this.drawerBack = this.navbar.querySelector("[data-navbar-drawer-back]");
    this.drawerSections =
      this.drawer?.querySelectorAll("[data-drawer-section]") ?? [];

    // Cache all dropdown elements
    this.dropdowns = this.navbar.querySelectorAll("[data-navbar-dropdown]");

    // Profile related elements
    this.profileSection = this.navbar.querySelector("[data-navbar-profile]");
    this.profileImage = this.navbar.querySelector(
      "[data-navbar-profile-image] img"
    );
    this.profileName = this.navbar.querySelector("[data-navbar-profile-name]");
    this.signOut = this.navbar.querySelector("[data-navbar-signout]");
    this.signIn = this.navbar.querySelector("[data-navbar-signin]");
    this.signUp = this.navbar.querySelector("[data-navbar-signup]");
    this.profileToggle = this.navbar.querySelector(
      "[data-navbar-profile-link]"
    );
    this.profileDropdown = this.navbar.querySelector(
      "[data-navbar-profile-dropdown]"
    );
    this.userDrawerTitle = this.navbar.querySelector(
      '[data-drawer-section="user"] .navbar__drawer-section-title'
    );

    // Cache common elements and prepare animation
    this.logo = this.navbar.querySelector("[data-navbar-logo]");

    // Cache logo icons for animation
    if (this.logo) {
      this.logoIcons = this.logo.querySelectorAll(".icon");
    }
  }

  initCriticalFeatures() {
    // Initialize only critical navigation features first
    this.bindToggler();
    this.bindEvents();
    this.animateNavbarOnLoad();
    this.handleResize(); // Immediate check for mobile/desktop
  }

  initNonCriticalFeatures() {
    // Initialize user profile and other non-critical features later
    this.setupUserProfile();

    // Remove will-change hint after animation is done
    if (this.logoIcons && this.logoIcons.length) {
      setTimeout(() => {
        this.logoIcons.forEach((icon) => {
          icon.style.willChange = "auto";
        });
      }, 1000); // After logo animation
    }

    // Performance mark for complete initialization
    performance.mark("navbar-complete");
    performance.measure(
      "navbar-total-init",
      "navbar-init-start",
      "navbar-complete"
    );
  }

  bindEvents() {
    // Use event delegation where possible to reduce event listener count

    // Global event delegation for all navbar interactions
    this.navbar.addEventListener("click", this.handleNavbarEvent.bind(this));

    // Add keyboard events for section toggles
    this.sectionToggles.forEach((toggle) => {
      toggle.addEventListener(
        "keydown",
        this.handleSectionToggleKeydown.bind(this)
      );
    });

    // Add keyboard event for profile toggle
    if (this.profileToggle) {
      this.profileToggle.addEventListener(
        "keydown",
        this.handleProfileToggleKeydown.bind(this)
      );
    }

    // Combined document event listeners for better performance
    document.addEventListener("keydown", this.handleGlobalKeyEvents.bind(this));
    document.addEventListener("click", this.handleOutsideClick.bind(this));

    // Throttled resize handler using requestAnimationFrame
    window.addEventListener("resize", this.handleResizeThrottled.bind(this), {
      passive: true,
    });
  }

  handleNavbarEvent(e) {
    // Delegate all navbar click events through a single handler
    const target = e.target;

    // Find closest interactive elements
    const toggle = target.closest("[data-navbar-section-toggle]");
    const backBtn = target.closest("[data-navbar-back-btn]");
    const drawerTrigger = target.closest("[data-navbar-drawer-trigger]");
    const profileLink = target.closest("[data-navbar-profile-link]");
    const drawerBackBtn = target.closest("[data-navbar-drawer-back]");

    // Handle section toggle clicks
    if (toggle && !this.isMobile()) {
      e.stopPropagation();
      const targetId = toggle.getAttribute("data-target");
      const dropdown = this.navbar.querySelector(`#${targetId}`);
      if (dropdown) {
        const isExpanded = toggle.getAttribute("aria-expanded") === "true";

        // Close all dropdowns first (which also blurs all toggles)
        this.closeAllDropdowns();

        // Only open the dropdown if it was previously closed
        if (!isExpanded) {
          this.toggleDropdown(toggle, dropdown, true, false);
        } else {
          // After closing, explicitly blur the toggle to prevent lingering active states
          toggle.blur();
        }
      }
    }

    // Handle back button clicks in dropdown
    if (backBtn) {
      const dropdown = backBtn.closest("[data-navbar-dropdown]");
      if (dropdown) {
        this.closeDropdown(dropdown);
      }
    }

    // Handle mobile drawer back button
    if (drawerBackBtn) {
      e.preventDefault();
      this.closeDrawer();
    }

    // Handle drawer trigger clicks
    if (drawerTrigger && this.isMobile()) {
      const drawerTarget = drawerTrigger.getAttribute("data-target-drawer");
      if (drawerTarget && this.drawer) {
        e.preventDefault();
        this.openDrawer(drawerTarget.replace(/^dropdown-/, ""));
      }
    }

    // Handle profile toggle clicks
    if (profileLink) {
      e.preventDefault();
      if (this.isMobile()) {
        this.openDrawer("user");
      } else {
        this.toggleProfileDropdown(false);
      }
    }
  }

  handleGlobalKeyEvents(e) {
    // Handle escape key
    if (e.key === "Escape") {
      this.handleGlobalKeydown(e);
    }

    // Handle tab key for focus trapping
    if (e.key === "Tab") {
      this.handleTabKey(e);
    }
  }

  handleGlobalKeydown(e) {
    if (e.key === "Escape") {
      if (this.currentActiveDropdown) {
        // If there's an active dropdown, close it and restore focus
        const toggle =
          this.navbar.querySelector(
            `[data-navbar-section-toggle][data-target="${this.currentActiveDropdown.id}"]`
          ) || this.profileToggle;

        if (toggle) {
          this.closeAllDropdowns();
          // Clear all active/focus states on every escape press
          // Don't restore focus - this is likely causing the lingering active state
          toggle.blur();
          this.currentActiveDropdown = null;
          this.focusTrapActive = false;
        }
      } else {
        this.closeAllDropdowns();
        this.closeNavbar();
      }
    }
  }

  handleOutsideClick(e) {
    // Check if click was outside navbar interactive elements
    const isClickInsideDropdown = this.isClickInsideElement(
      e,
      "[data-navbar-dropdown]"
    );
    const isClickOnSectionToggle = this.isClickOnToggle(e);
    const isClickOnProfileArea = this.isClickOnProfileArea(e);

    if (
      !isClickInsideDropdown &&
      !isClickOnSectionToggle &&
      !isClickOnProfileArea
    ) {
      this.closeAllDropdowns();

      // Explicitly blur any active toggle buttons when clicking outside
      this.sectionToggles.forEach((toggle) => {
        toggle.blur();
      });

      if (this.profileToggle) {
        this.profileToggle.blur();
      }
    }

    if (this.profileSection && !this.profileSection.contains(e.target)) {
      this.profileDropdown?.classList.remove("show");
      this.profileToggle?.setAttribute("aria-expanded", "false");
      this.profileToggle?.blur(); // Explicitly remove focus
      this.menu?.classList.remove("navbar__sections--dropdown-open");
    }
  }

  handleResizeThrottled() {
    // Use requestAnimationFrame for smoother visual updates
    if (this.resizeThrottleId) {
      return;
    }

    this.resizeThrottleId = requestAnimationFrame(() => {
      this.handleResize();
      this.resizeThrottleId = null;
    });
  }

  bindToggler() {
    if (!this.toggler) return;

    this.toggler.addEventListener("click", () => this.toggleNavbar());
    this.toggler.addEventListener("keydown", (e) =>
      this.handleKeyToggle(e, this.toggleNavbar.bind(this))
    );
  }

  isClickInsideElement(e, selector) {
    const elements = this.navbar.querySelectorAll(selector);
    return Array.from(elements).some((el) => el.contains(e.target));
  }

  isClickOnToggle(e) {
    return Array.from(this.sectionToggles).some((toggle) =>
      toggle.contains(e.target)
    );
  }

  isClickOnProfileArea(e) {
    return (
      this.profileToggle?.contains(e.target) ||
      this.profileDropdown?.contains(e.target)
    );
  }

  // Get user cookie - independent of window.octopus
  getUserCookie() {
    try {
      // Simplified cookie handling
      const cookie =
        typeof Cookies !== "undefined"
          ? Cookies.get("OctopusSignedInUser")
          : document.cookie.match(/(^| )OctopusSignedInUser=([^;]+)/)?.[2];

      return cookie ? decodeURIComponent(cookie) : null;
    } catch (e) {
      console.error("Error accessing user cookie:", e);
      return null;
    }
  }

  // Check if user is signed in
  isUserSignedIn() {
    return !!this.getUserCookie();
  }

  setupUserProfile() {
    performance.mark("profile-setup-start");

    // Use our self-contained method instead of window.octopus
    const cookie = this.getUserCookie();
    const isSignedIn = !!cookie;

    if (isSignedIn) {
      try {
        const userData = JSON.parse(cookie);
        this.handleSignedInUser(cookie);
      } catch (e) {
        console.error("Error parsing user cookie:", e);
        this.handleSignedOutUser();
      }
    } else {
      this.handleSignedOutUser();
    }

    // Profile dropdown setup
    if (this.profileToggle && this.profileDropdown) {
      const profileTargetId = "dropdown-user";
      this.profileDropdown.id = profileTargetId;
      this.profileToggle.setAttribute("data-target", profileTargetId);
    }

    performance.mark("profile-setup-end");
    performance.measure(
      "profile-setup",
      "profile-setup-start",
      "profile-setup-end"
    );
  }

  handleSignedInUser(cookie) {
    if (!this.profileSection) return;

    try {
      const user = JSON.parse(cookie);
      const name = user.fullName || user.email;

      this.updateProfileUI(user, name);
      this.updateDrawerTitle(name);
      this.updateAuthenticationState(true);
      this.trackUserIdentity(user);
    } catch (e) {
      console.error("Error processing signed-in user:", e);
    }
  }

  updateProfileUI(user, name) {
    if (this.profileImage) {
      this.profileImage.src = user.profileImageUrl || "";
      this.profileImage.alt = name;
      this.profileImage.title = name;
      this.profileImage.hidden = !user.profileImageUrl;
    }

    if (this.profileName) {
      this.profileName.textContent = this.shortenName(name);
    }
  }

  updateDrawerTitle(name) {
    if (this.userDrawerTitle) {
      this.userDrawerTitle.textContent = name;
    }
  }

  updateAuthenticationState(isSignedIn) {
    if (this.signOut) {
      this.signOut.removeAttribute("hidden");
      this.signOut.style.display = isSignedIn ? "flex" : "none";
    }

    if (this.profileSection) this.profileSection.hidden = !isSignedIn;
    if (this.signIn) this.signIn.hidden = isSignedIn;
    if (this.signUp) this.signUp.hidden = isSignedIn;
  }

  trackUserIdentity(user) {
    // Only attempt tracking if the services are available

    // Track user in Drip if available
    if (window._dcq) {
      try {
        window._dcq = window._dcq || [];
        window._dcq.push(["identify", { email: user.email, prospect: true }]);
      } catch (e) {
        console.error("Error tracking user in Drip:", e);
      }
    }

    // Track user in FullStory if available
    if (window.FS) {
      try {
        FS.identify(user.userHash, {
          displayName: user.fullName,
          email: user.email,
        });
      } catch (e) {
        console.error("Error tracking user in FullStory:", e);
      }
    }
  }

  handleSignedOutUser() {
    if (this.signIn) this.signIn.hidden = false;
    if (this.signUp) this.signUp.hidden = false;
    if (this.profileSection) this.profileSection.hidden = true;
    if (this.signOut) {
      this.signOut.removeAttribute("hidden");
      this.signOut.style.display = "none";
    }
  }

  toggleDropdown(toggle, dropdown, show, viaKeyboard = false) {
    this.commonDropdownToggle({
      toggle,
      dropdown,
      show,
      viaKeyboard,
    });
  }

  toggleProfileDropdown(viaKeyboard = false) {
    const isCurrentlyOpen = this.profileDropdown.classList.contains("show");
    this.closeAllDropdowns({ skipProfile: true });

    this.commonDropdownToggle({
      toggle: this.profileToggle,
      dropdown: this.profileDropdown,
      show: !isCurrentlyOpen,
      viaKeyboard,
    });
  }

  // Common functionality for dropdown toggling
  commonDropdownToggle({ toggle, dropdown, show, viaKeyboard = false }) {
    toggle.setAttribute("aria-expanded", show.toString());
    dropdown.classList.toggle("show", show);
    this.menu.classList.toggle("navbar__sections--dropdown-open", show);

    // Call positionDropdown to position the dropdown when opened
    if (show && !this.isMobile()) {
      // Use requestAnimationFrame for smoother visual updates
      requestAnimationFrame(() => {
        this.positionDropdown(dropdown);
      });
    }

    // Manage focus for accessibility
    if (show) {
      // Store currently focused element and active dropdown
      this.lastFocusedElement = document.activeElement;
      this.currentActiveDropdown = dropdown;
      this.focusTrapActive = true;

      // Only move focus into the dropdown if opened via keyboard
      if (viaKeyboard) {
        setTimeout(() => {
          const focusableElements = this.getFocusableElements(dropdown);
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          }
        }, 50); // Small delay to ensure dropdown is visible
      }
    } else {
      // When closing, don't restore focus - this prevents lingering active states
      this.lastFocusedElement = null;
      this.currentActiveDropdown = null;
      this.focusTrapActive = false;

      // Blur the toggle when closing to fully remove active states
      toggle.blur();
    }
  }

  handleKeyToggle(e, callback) {
    if (["Enter", " "].includes(e.key)) {
      e.preventDefault();
      callback();
    }
  }

  isMobile() {
    return window.innerWidth <= this.MOBILE_MAX_WIDTH;
  }

  toggleNavbar() {
    const isOpen = this.menu.classList.contains("show");
    isOpen ? this.closeNavbar() : this.openNavbar();
  }

  openNavbar() {
    performance.mark("navbar-open-start");

    this.menu.classList.add("show");
    this.toggler.setAttribute("aria-expanded", "true");
    this.toggler.classList.add("is-active");
    this.updateNavbarIcons(true);
    this.lockScrollIfMobile(true);
    this.isMenuOpen = true;

    // Show shadow element when mobile menu opens
    if (this.shadowElement && this.isMobile()) {
      const navbarHeight = this.navbar.offsetHeight;
      this.shadowElement.style.display = "block";
      this.shadowElement.style.height = `${navbarHeight}px`;
    }

    performance.mark("navbar-open-end");
    performance.measure("navbar-open", "navbar-open-start", "navbar-open-end");
  }

  closeNavbar() {
    this.menu.classList.remove("show", "navbar__sections--dropdown-open");
    this.toggler?.setAttribute("aria-expanded", "false");
    this.toggler?.classList.remove("is-active");
    this.updateNavbarIcons(false);
    this.closeDrawer(true);

    // Make sure to reset any overflow and touch properties
    if (this.menu) {
      this.menu.style.overflow = "";
      this.menu.style.overflowY = "";
      this.menu.style.touchAction = "";

      // Restore original height if it was modified
      if (this._originalMenuHeight !== undefined) {
        this.menu.style.height = this._originalMenuHeight;
        this._originalMenuHeight = undefined;
      } else {
        this.menu.style.height = "";
      }
    }

    this.lockScrollIfMobile(false);
    this.isMenuOpen = false;

    // Hide shadow element when mobile menu closes
    if (this.shadowElement) {
      this.shadowElement.style.display = "none";
    }
  }

  updateNavbarIcons(isOpen) {
    if (this.iconOpen && this.iconClose) {
      this.iconOpen.hidden = isOpen;
      this.iconClose.hidden = !isOpen;
    }
  }

  closeDrawer(immediate = false) {
    if (!this.drawer) return;

    this.drawer.classList.remove("show");
    this.isDrawerOpen = false;

    // We only hide the shadow element if the mobile menu is also closed
    // If this is called from closeNavbar(), the shadow will be hidden there
    if (this.shadowElement && !this.isMenuOpen) {
      this.shadowElement.style.display = "none";
    }

    // Re-enable scrolling in the main menu - reset all properties we modified
    if (this.menu && this.menu.classList.contains("show")) {
      this.menu.style.overflow = "";
      this.menu.style.overflowY = "";
      this.menu.style.touchAction = "";

      // Restore original height
      if (this._originalMenuHeight !== undefined) {
        this.menu.style.height = this._originalMenuHeight;
        this._originalMenuHeight = undefined;
      } else {
        // Fallback if original height wasn't stored
        this.menu.style.height = "";
      }
    }

    const hideDrawer = () => {
      this.drawer.style.visibility = "hidden";
      this.drawer.classList.add("navbar__drawer--hidden");
      this.drawerSections.forEach((section) => {
        section.style.display = "none";
      });
    };

    immediate ? hideDrawer() : setTimeout(hideDrawer, 500);
  }

  closeAllDropdowns({ skipProfile = false } = {}) {
    // Reset all toggle buttons
    this.sectionToggles.forEach((toggle) => {
      toggle.setAttribute("aria-expanded", "false");
      toggle.blur(); // Force remove focus
    });

    this.dropdowns.forEach((dd) => {
      dd.classList.remove("show");
    });

    if (!skipProfile && this.profileToggle && this.profileDropdown) {
      this.profileDropdown.classList.remove("show");
      this.profileToggle.setAttribute("aria-expanded", "false");
      this.profileToggle.blur(); // Force remove focus
    }

    this.menu.classList.remove("navbar__sections--dropdown-open");

    // Reset focus management but don't restore focus - this prevents lingering active states
    if (this.focusTrapActive) {
      this.lastFocusedElement = null;
      this.currentActiveDropdown = null;
      this.focusTrapActive = false;

      // Clear any active element to avoid persistent focus states
      if (document.activeElement) {
        document.activeElement.blur();
      }
    }
  }

  closeDropdown(dropdown) {
    dropdown.classList.remove("show");
    this.menu.classList.remove("navbar__sections--dropdown-open");

    const toggle = this.navbar.querySelector(
      `[data-navbar-section-toggle][data-target="${dropdown.id}"]`
    );

    if (toggle) {
      toggle.setAttribute("aria-expanded", "false");
      toggle.blur(); // Explicitly remove focus from the toggle
    }

    // Reset current active dropdown if this was it
    if (dropdown === this.currentActiveDropdown) {
      this.currentActiveDropdown = null;
      this.focusTrapActive = false;
    }
  }

  lockScrollIfMobile(shouldLock) {
    if (this.isMobile()) {
      document.body.classList.toggle("no-scroll", shouldLock);
    }
  }

  openMobileDrawer(targetSection) {
    // First, ensure menu is scrolled to top before any animations or transitions
    if (this.menu && this.menu.classList.contains("show")) {
      // Multiple scroll attempts with different timing
      this.forceScrollToTop(this.menu);
    }

    this.drawer.classList.remove("navbar__drawer--hidden");
    this.drawer.style.visibility = "visible";

    // Show shadow element
    if (this.shadowElement && this.isMobile()) {
      const navbarHeight = this.navbar.offsetHeight;
      this.shadowElement.style.height = `${navbarHeight}px`;
    }

    // Use requestAnimationFrame for smooth animation
    requestAnimationFrame(() => {
      this.drawer.classList.add("show");

      // Try to scroll again after drawer is showing
      if (this.menu && this.menu.classList.contains("show")) {
        this.forceScrollToTop(this.menu);
      }
    });

    this.isDrawerOpen = true;

    this.drawerSections.forEach((section) => {
      const sectionId = section.getAttribute("data-drawer-section");
      section.style.display = sectionId === targetSection ? "block" : "none";
    });

    // More aggressive approach to force scrolling to top and prevent scrolling on mobile
    if (this.menu && this.menu.classList.contains("show")) {
      // Then lock scrolling - multiple approaches for better mobile support
      this.menu.style.overflow = "hidden";
      this.menu.style.overflowY = "hidden";

      // Force touch-action to none to prevent mobile scroll
      this.menu.style.touchAction = "none";

      // Store original height and set fixed height to prevent elastic scrolling on iOS
      this._originalMenuHeight = this.menu.style.height;
      this.menu.style.height = `${this.menu.offsetHeight}px`;
    }

    this.lockScrollIfMobile(true);
  }

  // Helper method to forcefully scroll an element to top with various techniques
  forceScrollToTop(element) {
    // Immediate attempts
    element.scrollTo(0, 0);
    element.scrollTop = 0;

    // Force layout recalculation
    void element.offsetHeight;

    // Additional attempts with setTimeout for when transitions might interfere
    setTimeout(() => {
      element.scrollTo(0, 0);
      element.scrollTop = 0;

      // Some mobile browsers need multiple attempts
      setTimeout(() => {
        element.scrollTo(0, 0);
        element.scrollTop = 0;

        // Final attempt with scroll behavior modification
        const originalScrollBehavior = element.style.scrollBehavior;
        element.style.scrollBehavior = "auto";
        element.scrollTo(0, 0);
        element.scrollTop = 0;
        element.style.scrollBehavior = originalScrollBehavior;
      }, 50);
    }, 10);
  }

  handleResize() {
    const isNowMobile = this.isMobile();

    if (this.wasMobile !== isNowMobile) {
      this.wasMobile = isNowMobile;
      this.resetNavigationState();
    } else if (!isNowMobile) {
      // Reposition any open dropdowns when the window is resized in desktop mode
      const openDropdowns = this.navbar.querySelectorAll(
        ".navbar__dropdown.show"
      );
      if (openDropdowns.length > 0) {
        openDropdowns.forEach((dropdown) => {
          this.positionDropdown(dropdown);
        });
      }
    } else if (isNowMobile && (this.isMenuOpen || this.isDrawerOpen)) {
      // Update shadow element height when in mobile mode and menu/drawer is open
      this.updateShadowElementHeight();
    }
  }

  resetNavigationState() {
    this.closeNavbar();
    this.closeAllDropdowns();
    this.closeDrawer(true);
  }

  shortenName(name) {
    const parts = name.trim().split(" ");
    return parts.length > 1 ? `${parts[0]} ${parts[1][0]}.` : parts[0];
  }

  animateNavbarOnLoad() {
    if (document.readyState === "complete") {
      this.animateNavbar();
    } else {
      window.addEventListener(
        "load",
        () => {
          this.animateNavbar();
        },
        { once: true }
      );
    }
  }

  animateNavbar() {
    if (this.navbar) {
      this.navbar.classList.add("navbar-pu--animate");
    }
  }

  handleTabKey(e) {
    // Only process for Tab key and when we have an active dropdown
    if (e.key !== "Tab" || !this.currentActiveDropdown) return;

    // Find all focusable elements within the dropdown
    const focusableElements = this.getFocusableElements(
      this.currentActiveDropdown
    );
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Handle Tab and Shift+Tab to create a focus trap loop
    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus(); // Loop back to last element
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus(); // Loop back to first element
    }
  }

  getFocusableElements(container) {
    // Get all focusable elements within a container
    const focusableSelectors = [
      "a[href]:not([disabled])",
      "button:not([disabled])",
      "textarea:not([disabled])",
      'input[type="text"]:not([disabled])',
      'input[type="radio"]:not([disabled])',
      'input[type="checkbox"]:not([disabled])',
      "select:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ];

    return (
      Array.from(container.querySelectorAll(focusableSelectors.join(",")))
        // More efficient way to check visibility using offsetParent
        .filter((el) => el.offsetParent !== null)
    );
  }

  // Handle keyboard events for section toggles (Enter/Space)
  handleSectionToggleKeydown(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();

      const toggle = e.currentTarget;
      const targetId = toggle.getAttribute("data-target");
      const dropdown = this.navbar.querySelector(`#${targetId}`);

      if (dropdown && !this.isMobile()) {
        const isExpanded = toggle.getAttribute("aria-expanded") === "true";
        this.closeAllDropdowns();
        this.toggleDropdown(toggle, dropdown, !isExpanded, true); // Via keyboard
      } else if (this.isMobile() && toggle.hasAttribute("data-target-drawer")) {
        // For mobile drawer
        const drawerTarget = toggle.getAttribute("data-target-drawer");
        if (drawerTarget && this.drawer) {
          this.openDrawer(drawerTarget.replace(/^dropdown-/, ""));
        }
      }
    }
  }

  // Handle keyboard events for profile toggle (Enter/Space)
  handleProfileToggleKeydown(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();

      if (this.isMobile()) {
        this.openDrawer("user");
      } else {
        this.toggleProfileDropdown(true); // Via keyboard
      }
    }
  }

  // Positions the dropdown to prevent viewport overflow
  positionDropdown(dropdown) {
    // Skip if we're in mobile view
    if (this.isMobile()) {
      return;
    }

    // Define safety margin
    const safetyMargin = 32; // 32px from viewport edges

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;

    // Get dropdown dimensions and position
    const dropdownRect = dropdown.getBoundingClientRect();

    // Check distances to viewport edges
    const distanceToRight = viewportWidth - dropdownRect.right;

    // Check for overflow
    const overflowsRight = distanceToRight < 0;

    // Apply positioning fixes
    if (overflowsRight) {
      const dropdownMarker = dropdown.querySelector(".icon--marker-navbar");
      const translateValue = Math.abs(distanceToRight) + safetyMargin;
      const markerPosition = 32 + translateValue;
      dropdown.style.transform = `scale(1) translateY(0) translateX(-${translateValue}px)`;
      dropdownMarker.style.left = `${markerPosition}px`;
    }
  }

  // Opens a specific drawer section
  openDrawer(targetSection) {
    // This is just a wrapper around openMobileDrawer for backwards compatibility
    this.openMobileDrawer(targetSection);
  }

  // Create a shadow element that will be displayed when drawer is open
  createShadowElement() {
    this.shadowElement = document.createElement("div");
    this.shadowElement.classList.add("shadow-element");
    this.shadowElement.style.position = "fixed";
    this.shadowElement.style.top = "0";
    this.shadowElement.style.left = "0";
    this.shadowElement.style.right = "0";
    this.shadowElement.style.boxShadow = "0px 4px 20px 0px rgba(0, 0, 0, 0.10)";
    this.shadowElement.style.zIndex = "10000";
    this.shadowElement.style.pointerEvents = "none";
    this.shadowElement.style.display = "none";

    // Add to document body
    document.body.appendChild(this.shadowElement);
  }

  // Clean up shadow element on page unload
  destroyShadowElement() {
    if (this.shadowElement && document.body.contains(this.shadowElement)) {
      document.body.removeChild(this.shadowElement);
    }
  }

  // Helper method to update shadow element height
  updateShadowElementHeight() {
    if (this.shadowElement && (this.isMenuOpen || this.isDrawerOpen)) {
      const navbarHeight = this.navbar.offsetHeight;
      this.shadowElement.style.height = `${navbarHeight}px`;
    }
  }
}

// Check if requestIdleCallback is supported, if not, polyfill it
if (!window.requestIdleCallback) {
  window.requestIdleCallback = function (callback, options) {
    const timeout = options?.timeout || 1;
    return setTimeout(() => {
      callback({
        didTimeout: false,
        timeRemaining: function () {
          return Math.max(0, 50);
        },
      });
    }, timeout);
  };
}

// Initialize the navbar
document.addEventListener("DOMContentLoaded", () => {
  const navbars = document.querySelectorAll("[data-navbar-pu]");
  navbars.forEach((element) => {
    new NavbarPU(element);
  });
});

// Also initialize if DOM is already loaded
// This dual-initialization pattern ensures your navbar will initialize correctly regardless of when the script loads relative to the DOM being ready. It's a common defensive programming technique for critical UI components.
// Without this fallback, if your script loads after the DOMContentLoaded event, your navbar might never initialize at all.
if (document.readyState !== "loading") {
  const navbars = document.querySelectorAll("[data-navbar-pu]");
  navbars.forEach((element) => {
    new NavbarPU(element);
  });
}
